import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from './firebase';

export const FEEDBACK_COLLECTION = 'feedback';
export const FEEDBACK_TYPES = ['general', 'bug', 'idea'];
export const FEEDBACK_STATUSES = ['unread', 'read'];
export const FEEDBACK_LOCALES = ['es', 'en', 'pt'];

const MAX_MESSAGE_LENGTH = 2000;
const MAX_EMAIL_LENGTH = 200;
const MAX_PAGE_URL_LENGTH = 500;
const MAX_USER_AGENT_LENGTH = 300;

const DISCORD_WEBHOOK_URL = process.env.REACT_APP_DISCORD_FEEDBACK_WEBHOOK || '';
const DISCORD_TYPE_META = {
  general: { emoji: '💬', label: 'General', color: 0x22d3ee },
  bug: { emoji: '🐛', label: 'Bug', color: 0xfb7185 },
  idea: { emoji: '💡', label: 'Idea', color: 0xfbbf24 },
};

const getCollectionRef = () => collection(db, FEEDBACK_COLLECTION);
const mapFeedbackDoc = (docSnap) => ({ id: docSnap.id, ...docSnap.data() });

// Best-effort only: a ping in Discord when new feedback comes in. Never allowed to
// affect the actual submission - any failure here is swallowed and just logged.
const notifyDiscord = (feedback) => {
  if (!DISCORD_WEBHOOK_URL) return;

  const meta = DISCORD_TYPE_META[feedback.type] || DISCORD_TYPE_META.general;
  const fields = [];
  if (feedback.email) fields.push({ name: 'Email', value: feedback.email, inline: true });
  if (feedback.pageUrl) fields.push({ name: 'Page', value: feedback.pageUrl, inline: true });

  fetch(DISCORD_WEBHOOK_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      embeds: [{
        title: `${meta.emoji} New ${meta.label} feedback`,
        description: feedback.message.slice(0, 1000),
        color: meta.color,
        fields,
        timestamp: new Date().toISOString(),
      }],
    }),
  }).catch((error) => {
    console.error('Discord feedback notification failed:', error);
  });
};

export const submitFeedback = async ({ type, message, email = '', locale = 'en' } = {}) => {
  if (!db) {
    throw new Error('Firestore is not configured.');
  }

  const trimmedMessage = String(message || '').trim();
  if (!trimmedMessage) {
    throw new Error('Please write a message before sending.');
  }
  if (!FEEDBACK_TYPES.includes(type)) {
    throw new Error('Invalid feedback type.');
  }

  const feedback = {
    type,
    message: trimmedMessage.slice(0, MAX_MESSAGE_LENGTH),
    email: String(email || '').trim().slice(0, MAX_EMAIL_LENGTH),
    status: 'unread',
    pageUrl: `${window.location.pathname}${window.location.search}`.slice(0, MAX_PAGE_URL_LENGTH),
    userAgent: String(window.navigator?.userAgent || '').slice(0, MAX_USER_AGENT_LENGTH),
    locale: FEEDBACK_LOCALES.includes(locale) ? locale : 'en',
  };

  await addDoc(getCollectionRef(), {
    ...feedback,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  notifyDiscord(feedback);
};

export const listFeedback = async ({ status = 'all' } = {}) => {
  if (!db) return [];

  const constraints = status !== 'all' ? [where('status', '==', status)] : [];
  constraints.push(orderBy('createdAt', 'desc'));

  const snapshot = await getDocs(query(getCollectionRef(), ...constraints));
  return snapshot.docs.map(mapFeedbackDoc);
};

export const markFeedbackStatus = async (feedbackId, status) => {
  if (!db) return;
  if (!FEEDBACK_STATUSES.includes(status)) {
    throw new Error('Invalid feedback status.');
  }

  await updateDoc(doc(db, FEEDBACK_COLLECTION, feedbackId), {
    status,
    updatedAt: serverTimestamp(),
  });
};

export const deleteFeedbackEntry = async (feedbackId) => {
  if (!db) return;
  await deleteDoc(doc(db, FEEDBACK_COLLECTION, feedbackId));
};

// Live unread count for the admin badge. Returns an unsubscribe function.
export const subscribeUnreadFeedbackCount = (callback) => {
  if (!db) return () => {};

  const unreadQuery = query(getCollectionRef(), where('status', '==', 'unread'));
  return onSnapshot(
    unreadQuery,
    (snapshot) => callback(snapshot.size),
    () => callback(0)
  );
};
